import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  ArrowLeft,
  Flag,
  Heart,
  Loader2,
  MessageCircle,
  Package,
  Send,
  Share2,
} from "lucide-solid";

import { Link } from "@/lib/u-router";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  addCommentMutation,
  followUserMutation,
  getFollowEdgeQuery,
  getPostByIdQuery,
  getPostCommentsQuery,
  getPostLikesQuery,
  likedPostIds,
  parseMedia,
  queryKeys,
  relativeTime,
  toggleLikeMutation,
  unfollowUserMutation,
} from "@famidex/shared";
import { fetchComments, fetchPost } from "../data/community-rest";
import { formatRelative } from "./post-card";
import { ReportPostDialog } from "./report-post-dialog";
import { ShareSheet } from "./share-sheet";

interface DetailComment {
  id: string;
  authorName: string | null;
  authorImage: string | null;
  body: string;
  createdAt: string | null;
}

interface Detail {
  id: string;
  authorId: string | null;
  authorName: string | null;
  authorImage: string | null;
  caption: string | null;
  productId: string | null;
  productName: string | null;
  media: string[];
  createdAt: string | null;
}

/**
 * 📣 Detalle de publicación — media, pie, etiqueta de producto, likes,
 * comentarios (añadir), reportar y compartir. Local-first para el usuario con
 * cuenta (PowerSync), guest-first para el invitado (REST, sólo lectura).
 */
export function PostDetailPage(props: { postId: string }) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const { userId } = session;
  const isAuthed = () => session.isAuthenticated();

  const [commentText, setCommentText] = createSignal("");
  const [reportOpen, setReportOpen] = createSignal(false);
  const [shareOpen, setShareOpen] = createSignal(false);
  const [busy, setBusy] = createSignal(false);

  // Local-first (con cuenta)
  const postRows = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.post(props.postId),
    queryFn: () => {
      const d = drizzle();
      return d ? getPostByIdQuery(d, props.postId) : null;
    },
  });
  const commentRows = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.comments(props.postId),
    queryFn: () => {
      const d = drizzle();
      return d ? getPostCommentsQuery(d, props.postId) : null;
    },
  });
  const likeRows = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && isAuthed(),
    queryKey: () => queryKeys.community.postLikes(props.postId),
    queryFn: () => {
      const d = drizzle();
      return d ? getPostLikesQuery(d, props.postId) : null;
    },
  });

  // Guest (REST)
  const [postR] = createResource(
    () => (isAuthed() ? null : props.postId),
    async (id: string) => fetchPost(id),
  );
  const [commentsR] = createResource(
    () => (isAuthed() ? null : props.postId),
    async (id: string) => fetchComments(id),
  );

  const detail = createMemo<Detail | null>(() => {
    if (isAuthed()) {
      const p = postRows.data?.[0];
      return p
        ? {
            id: p.id,
            authorId: p.user_id,
            authorName: p.author_name,
            authorImage: p.author_image,
            caption: p.caption,
            productId: p.product_id,
            productName: null,
            media: parseMedia(p.media),
            createdAt: p.created_at,
          }
        : null;
    }
    const p = postR();
    return p
      ? {
          id: p.id,
          authorId: p.userId ?? null,
          authorName: p.authorName ?? null,
          authorImage: p.authorImage ?? null,
          caption: p.caption ?? null,
          productId: p.productId ?? null,
          productName: p.productName ?? null,
          media: p.media,
          createdAt: p.createdAt,
        }
      : null;
  });

  // 👥 Botón SEGUIR al autor (cableado Wave 2: la arista `follow` ya tenía
  // write-path + notificación server-side; faltaba la afordancia en la UI).
  const authorId = createMemo(() => detail()?.authorId ?? null);
  const canFollow = createMemo(() => {
    const author = authorId();
    return Boolean(isAuthed() && author && author !== userId());
  });
  const followEdge = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()) && canFollow(),
    queryKey: () => queryKeys.social.edge(userId(), authorId() ?? undefined),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      const author = authorId();
      return d && uid && author ? getFollowEdgeQuery(d, uid, author) : null;
    },
  });
  const isFollowing = createMemo(() => Boolean(followEdge.data?.[0]));

  const toggleFollow = async () => {
    const d = drizzle();
    const uid = userId();
    const author = authorId();
    if (!d || !uid || !author || busy()) {
      return;
    }
    setBusy(true);
    try {
      if (isFollowing()) {
        await unfollowUserMutation(d, uid, author);
      } else {
        await followUserMutation(d, uid, author, {
          followerName: session.data?.user?.name ?? null,
          followerImage: session.data?.user?.image ?? null,
          followeeName: detail()?.authorName ?? null,
          followeeImage: detail()?.authorImage ?? null,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const comments = createMemo<DetailComment[]>(() => {
    if (isAuthed()) {
      return (commentRows.data ?? []).map((c) => ({
        id: c.id,
        authorName: c.author_name,
        authorImage: c.author_image,
        body: c.body ?? "",
        createdAt: c.created_at,
      }));
    }
    return (commentsR() ?? []).map((c) => ({
      id: c.id,
      authorName: c.authorName ?? null,
      authorImage: c.authorImage ?? null,
      body: c.body,
      createdAt: c.createdAt,
    }));
  });

  const likeCount = createMemo(() =>
    isAuthed() ? (likeRows.data ?? []).length : (postR()?.likeCount ?? 0),
  );
  const liked = createMemo(() =>
    isAuthed() ? likedPostIds(likeRows.data ?? [], userId()).has(props.postId) : false,
  );

  const loading = () => (isAuthed() ? postRows.isLoading : postR.loading);

  const toggleLike = () => {
    const d = drizzle();
    const uid = userId();
    if (!d || !uid || !isAuthed()) {
      return;
    }
    void toggleLikeMutation(d, uid, props.postId);
  };

  const submitComment = async () => {
    const body = commentText().trim();
    const d = drizzle();
    const uid = userId();
    if (!body || !d || !uid || !isAuthed()) {
      return;
    }
    setBusy(true);
    try {
      await addCommentMutation(
        d,
        uid,
        { name: session.data?.user?.name ?? null, image: session.data?.user?.image ?? null },
        props.postId,
        body,
      );
      setCommentText("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex flex-col gap-5" data-testid="post-detail">
      <Link
        to="/community"
        class="inline-flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft class="h-4 w-4" />
        {t(msg`Comunidad`)}
      </Link>

      <Show
        when={!loading()}
        fallback={
          <div class="grid place-items-center py-20 text-muted-foreground">
            <Loader2 class="h-8 w-8 animate-spin" />
          </div>
        }
      >
        <Show
          when={detail()}
          fallback={
            <div class="grid place-items-center gap-2 py-20 text-center text-muted-foreground">
              <MessageCircle class="h-8 w-8" />
              <p class="text-sm">{t(msg`Esta publicación ya no está disponible.`)}</p>
            </div>
          }
        >
          {(post) => (
            <>
              {/* Autor + acciones */}
              <header class="flex items-center gap-3">
                <span class="grid h-10 w-10 place-items-center rounded-full bg-accent text-sm font-bold text-accent-foreground">
                  {(post().authorName?.[0] ?? "?").toUpperCase()}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold text-foreground">
                    {post().authorName ?? t(msg`Coleccionista`)}
                  </p>
                  <p class="text-xs text-muted-foreground">
                    {formatRelative(t, relativeTime(post().createdAt, Date.now()))}
                  </p>
                </div>
                <Show when={canFollow()}>
                  <button
                    type="button"
                    data-testid="follow-author"
                    disabled={busy()}
                    onClick={() => void toggleFollow()}
                    aria-pressed={isFollowing()}
                    class="rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60"
                    classList={{
                      "bg-primary text-primary-foreground": !isFollowing(),
                      "border border-border/70 bg-card text-muted-foreground": isFollowing(),
                    }}
                  >
                    <Show when={isFollowing()} fallback={t(msg`Seguir`)}>
                      {t(msg`Siguiendo`)}
                    </Show>
                  </button>
                </Show>
                <button
                  type="button"
                  data-testid="open-share"
                  onClick={() => setShareOpen(true)}
                  class="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-primary"
                  aria-label={t(msg`Compartir`)}
                >
                  <Share2 class="h-5 w-5" />
                </button>
                <Show when={isAuthed()}>
                  <button
                    type="button"
                    data-testid="open-report"
                    onClick={() => setReportOpen(true)}
                    class="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-destructive"
                    aria-label={t(msg`Reportar`)}
                  >
                    <Flag class="h-5 w-5" />
                  </button>
                </Show>
              </header>

              {/* Media */}
              <Show when={post().media.length > 0}>
                <div class="flex gap-2 overflow-x-auto rounded-2xl">
                  <For each={post().media}>
                    {(url) => (
                      <img
                        src={url}
                        alt=""
                        class="aspect-square w-full max-w-full shrink-0 rounded-2xl border border-border/60 object-cover"
                      />
                    )}
                  </For>
                </div>
              </Show>

              {/* Pie + producto */}
              <Show when={post().caption}>
                <p class="whitespace-pre-line text-sm leading-relaxed text-foreground">
                  {post().caption}
                </p>
              </Show>
              <Show when={post().productId}>
                {(pid) => (
                  <Link
                    to="/community/product/$productId"
                    params={{ productId: pid() }}
                    class="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground"
                  >
                    <Package class="h-3.5 w-3.5" />
                    {post().productName ?? t(msg`Ver set`)}
                  </Link>
                )}
              </Show>

              {/* Like */}
              <div class="flex items-center gap-5 border-y border-border/50 py-3 text-muted-foreground">
                <button
                  type="button"
                  data-testid="detail-like"
                  disabled={!isAuthed()}
                  onClick={toggleLike}
                  class="flex items-center gap-1.5 text-sm hover:text-primary disabled:opacity-70"
                  classList={{ "text-primary": liked() }}
                  aria-pressed={liked()}
                >
                  <Heart class="h-5 w-5" classList={{ "fill-primary": liked() }} />
                  <span class="tabular-nums">{likeCount()}</span>
                </button>
                <span class="flex items-center gap-1.5 text-sm">
                  <MessageCircle class="h-5 w-5" />
                  <span class="tabular-nums">{comments().length}</span>
                </span>
              </div>

              {/* Comentarios */}
              <section class="flex flex-col gap-3">
                <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Comentarios`)}
                </h2>
                <Show
                  when={comments().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground">
                      {t(msg`Sé el primero en comentar.`)}
                    </p>
                  }
                >
                  <ul class="flex flex-col gap-3" data-testid="comment-list">
                    <For each={comments()}>
                      {(c) => (
                        <li class="flex gap-3">
                          <span class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                            {(c.authorName?.[0] ?? "?").toUpperCase()}
                          </span>
                          <div class="min-w-0 flex-1 rounded-xl bg-card/70 px-3 py-2">
                            <p class="text-xs font-semibold text-foreground">
                              {c.authorName ?? t(msg`Coleccionista`)}
                            </p>
                            <p class="text-sm text-foreground">{c.body}</p>
                          </div>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>

                <Show
                  when={isAuthed()}
                  fallback={
                    <Link
                      to="/login"
                      class="mt-1 w-fit rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
                    >
                      {t(msg`Inicia sesión para comentar`)}
                    </Link>
                  }
                >
                  <div class="mt-1 flex items-center gap-2">
                    <input
                      type="text"
                      data-testid="comment-input"
                      value={commentText()}
                      onInput={(e) => setCommentText(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          void submitComment();
                        }
                      }}
                      placeholder={t(msg`Añade un comentario...`)}
                      class="min-w-0 flex-1 rounded-full border border-border/60 bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      data-testid="submit-comment"
                      disabled={busy() || !commentText().trim()}
                      onClick={() => void submitComment()}
                      class="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
                      aria-label={t(msg`Enviar`)}
                    >
                      <Send class="h-4 w-4" />
                    </button>
                  </div>
                </Show>
              </section>

              <ReportPostDialog
                postId={post().id}
                open={reportOpen()}
                onOpenChange={setReportOpen}
              />
              <ShareSheet
                open={shareOpen()}
                onOpenChange={setShareOpen}
                shareUrl={typeof globalThis.window === "undefined" ? "" : globalThis.location.href}
                post={{
                  id: post().id,
                  authorName: post().authorName,
                  caption: post().caption,
                  cover: post().media[0] ?? null,
                }}
              />
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
